<template>
  <div class="booktracking-table-wrapper">
    <PaginationControls
      :page-actuelle="navigationSettings.pageActuelle"
      :limite-actuelle="navigationSettings.limiteActuelle"
      :pages-total="nbPagesTotal"
      :limite-options="itemsPerPageOptions"
      :disabled="disablePagination"
      @update="handlePaginationUpdate"
    />
    <table :id="tableId">
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.verbose_name"
          >
            <span
              class="name"
              :class="{ sortable: col.sort_key !== undefined, }"
              @click="onSort(col)"
            >
              {{ col.verbose_name }}
              <i
                v-if="col.sort_key !== undefined"
                :class="sortIconClass(col)"
              />
            </span>
          </th>
          <th>{{ commandeCommentColumnLabel }}</th>
          <th>{{ transportCommentColumnLabel }}</th>
          <th v-if="showAlertColumn">
            {{ alertColumnLabel }}
          </th>
        </tr>
      </thead>
      <tbody v-if="!loading && items.length > 0">
        <tr
          v-for="commande in parsedItems"
          :key="commande.id"
          class="clickable-row"
          @mousedown="onRowMouseDown($event)"
          @click="onRowClick(commande, $event)"
          @dblclick="onRowDoubleClick"
        >
          <td
            v-for="col in columns"
            :key="col.verbose_name"
          >
            {{ col.val(commande) }}
          </td>
          <td class="center consult-cell">
            <b-button
              v-if="hasCommentsOfType(commande, 'ARS')"
              :id="buttonId(commande, 'ars')"
              class="comment-count-button"
              variant="primary"
              size="sm"
              @click.stop.prevent
              @mouseenter="onTooltipEnter(commande, 'ARS')"
              @focus="onTooltipEnter(commande, 'ARS')"
            >
              <i class="fas fa-comments" />
              {{ commentCount(commande, 'ARS') }}
            </b-button>
            <b-popover
              v-if="hasCommentsOfType(commande, 'ARS')"
              :target="buttonId(commande, 'ars')"
              placement="left"
              triggers="hover focus"
              custom-class="comment-popover"
            >
              <template v-if="tooltipStateByType(commande, 'ARS').status === 'loading'">
                <div class="comment-popover__loading">
                  <b-spinner
                    small
                    label="Chargement"
                  />
                  <span>Chargement du commentaire…</span>
                </div>
              </template>
              <template v-else-if="tooltipStateByType(commande, 'ARS').status === 'error'">
                <div class="comment-popover__message">
                  {{ tooltipStateByType(commande, 'ARS').error || "Impossible de charger le commentaire." }}
                </div>
              </template>
              <template v-else-if="hasTooltipComments(commande, 'ARS')">
                <div
                  v-for="(comment, index) in tooltipStateByType(commande, 'ARS').comments"
                  :key="comment.id || index"
                  class="comment-popover__item"
                >
                  <div class="comment-popover__header">
                    <slot
                      name="comment-header"
                      :comment="comment"
                    />
                  </div>
                  <p class="comment-popover__body">
                    <slot
                      name="comment-body"
                      :comment="comment"
                    >
                      {{ comment.commentaire }}
                    </slot>
                  </p>
                </div>
              </template>
              <template v-else>
                <div class="comment-popover__message">
                  Aucun commentaire.
                </div>
              </template>
            </b-popover>
          </td>
          <td class="center consult-cell">
            <span
              v-if="hasCommentsOfType(commande, 'LDE') || hasFeuVertAmbassade(commande)"
              class="transport-comment-indicator"
            >
              <b-button
                v-if="hasCommentsOfType(commande, 'LDE')"
                :id="buttonId(commande, 'transport')"
                class="comment-count-button"
                variant="primary"
                size="sm"
                @click.stop.prevent
                @mouseenter="onTooltipEnter(commande, 'LDE')"
                @focus="onTooltipEnter(commande, 'LDE')"
              >
                <i class="fas fa-comments" />
                {{ commentCount(commande, 'LDE') }}
              </b-button>
              <span
                v-if="hasFeuVertAmbassade(commande)"
                class="feu-vert-dot"
                aria-label="Feu vert Ambassade"
                title="Feu vert Ambassade"
              />
            </span>
            <b-popover
              v-if="hasCommentsOfType(commande, 'LDE')"
              :target="buttonId(commande, 'transport')"
              placement="left"
              triggers="hover focus"
              custom-class="comment-popover"
            >
              <template v-if="tooltipStateByType(commande, 'LDE').status === 'loading'">
                <div class="comment-popover__loading">
                  <b-spinner
                    small
                    label="Chargement"
                  />
                  <span>Chargement du commentaire…</span>
                </div>
              </template>
              <template v-else-if="tooltipStateByType(commande, 'LDE').status === 'error'">
                <div class="comment-popover__message">
                  {{
                    tooltipStateByType(commande, 'LDE').error || "Impossible de charger le commentaire."
                  }}
                </div>
              </template>
              <template v-else-if="hasTooltipComments(commande, 'LDE')">
                <div
                  v-for="(comment, index) in tooltipStateByType(commande, 'LDE').comments"
                  :key="comment.id || index"
                  class="comment-popover__item"
                >
                  <div class="comment-popover__header">
                    <slot
                      name="comment-header"
                      :comment="comment"
                    />
                  </div>
                  <p class="comment-popover__body">
                    <slot
                      name="comment-body"
                      :comment="comment"
                    >
                      {{ comment.commentaire }}
                    </slot>
                  </p>
                </div>
              </template>
              <template v-else>
                <div class="comment-popover__message">
                  Aucun commentaire.
                </div>
              </template>
            </b-popover>
          </td>
          <td
            v-if="showAlertColumn"
            class="center consult-cell"
          >
            <span
              v-if="Boolean(commande.alerte_bif_bip)"
              class="bif-bip-alert-icon"
              aria-label="Alerte BIF/BIP activée"
              title="Alerte BIF/BIP activée"
            >
              <i class="fas fa-exclamation-triangle" />
            </span>
          </td>
        </tr>
      </tbody>
      <tbody v-else-if="(!items.length || error) && !loading">
        <tr>
          <td
            :colspan="columns.length + extraColumnsCount"
            class="center"
          >
            {{ error || "Aucune donnée" }}
          </td>
        </tr>
      </tbody>
      <tbody
        v-else
        class="loading"
      >
        <tr>
          <td
            :colspan="columns.length + extraColumnsCount"
            class="center"
          >
            <b-spinner label="Chargement" />
          </td>
        </tr>
      </tbody>
    </table>
    <PaginationControls
      v-if="items.length > 0"
      :page-actuelle="navigationSettings.pageActuelle"
      :limite-actuelle="navigationSettings.limiteActuelle"
      :pages-total="nbPagesTotal"
      :limite-options="itemsPerPageOptions"
      @update="handlePaginationUpdate"
    />
  </div>
</template>

<script>
import PaginationControls from "./PaginationControls.vue";
import MixinSimpleRowClick from "../../../mixins/mixinSimpleRowClick";

const noop = () => undefined;
const defaultTooltipState = () => ({ status: "idle", comments: [], error: null });

export default {
  name: "BookTrackingTable",
  components: {
    PaginationControls,
  },
  mixins: [MixinSimpleRowClick],
  props: {
    tableId: {
      type: String,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    parsedItems: {
      type: Array,
      default: () => [],
    },
    error: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    navigationSettings: {
      type: Object,
      required: true,
    },
    nbPagesTotal: {
      type: Number,
      required: true,
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [25, 50, 100],
    },
    disablePagination: {
      type: Boolean,
      default: false,
    },
    orderBy: {
      type: String,
      default: null,
    },
    detailsColumnLabel: {
      type: String,
      default: "Détails",
    },
    commandeCommentColumnLabel: {
      type: String,
      default: "Infos NH",
    },
    transportCommentColumnLabel: {
      type: String,
      default: "Infos LDE",
    },
    alertColumnLabel: {
      type: String,
      default: "Alerte BIF/BIP",
    },
    showAlertColumn: {
      type: Boolean,
      default: true,
    },
    consultButtonId: {
      type: Function,
      default: (commande) => {
        const id = (commande && commande.id) ? commande.id : "row";
        return `consult-btn-${id}`;
      },
    },
    handleCommentTooltipEnter: {
      type: Function,
      default: noop,
    },
    commentTooltipState: {
      type: Function,
      default: defaultTooltipState,
    },
  },
  computed: {
    extraColumnsCount() {
      return this.showAlertColumn ? 3 : 2;
    },
  },
  methods: {
    handlePaginationUpdate(payload) {
      this.$emit("update-pagination", payload);
    },
    sortIconClass(col) {
      if (col.sort_key === this.orderBy) {
        return "fas fa-fw fa-sort-up";
      }
      if (`-${col.sort_key}` === this.orderBy) {
        return "fas fa-fw fa-sort-down";
      }
      return "fas fa-fw fa-sort";
    },
    onSort(col) {
      if (col.sort_key !== undefined) {
        this.$emit("sort", col.sort_key);
      }
    },
    openCommande(commande) {
      this.$emit("open-commande", commande);
    },
    onTooltipEnter(commande, typeCommentaire) {
      this.handleCommentTooltipEnter(commande, typeCommentaire);
    },
    buttonId(commande, suffix = "default") {
      return `${this.consultButtonId(commande)}-${suffix}`;
    },
    tooltipState(commande) {
      return this.commentTooltipState(commande) || defaultTooltipState();
    },
    isTransportComment(typeCommentaire) {
      return typeCommentaire === "LDE";
    },
    commentCount(commande, typeCommentaire) {
      if (!commande) return 0;
      if (this.isTransportComment(typeCommentaire)) {
        return commande.commentaires_lde || 0;
      }
      return commande.commentaires_ars || 0;
    },
    hasCommentsOfType(commande, typeCommentaire) {
      return this.commentCount(commande, typeCommentaire) > 0;
    },
    hasFeuVertAmbassade(commande) {
      return Boolean(commande && commande.feu_vert_ambassade);
    },
    tooltipStateByType(commande, typeCommentaire) {
      const state = this.tooltipState(commande);
      if (state.status === "loading" || state.status === "error") {
        return state;
      }

      const comments = (state.comments || []).filter((comment) => {
        const commentType = comment && comment.type_commentaire ? comment.type_commentaire : "ARS";
        return this.isTransportComment(typeCommentaire)
          ? commentType === "LDE"
          : commentType !== "LDE";
      });

      return {
        ...state,
        comments,
      };
    },
    hasTooltipComments(commande, typeCommentaire) {
      const state = this.tooltipStateByType(commande, typeCommentaire);
      return Boolean(state.comments && state.comments.length);
    },
    handleSimpleRowClick(commande) {
      this.openCommande(commande);
    },
  },
};
</script>
